import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NgProgressService } from '@shared/services/ng-progress.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ProgressBarModule } from 'primeng/progressbar';
import { SkeletonModule } from 'primeng/skeleton';
import { Subscription, combineLatest, filter } from 'rxjs';
import { ProfileService } from '../../profile/profile.service';
import {
  Rewards,
  InvestementTireType,
  InvestementTire,
} from '../rewards.model';
import { RewardsService } from '../rewards.service';
import { CommonModule, DecimalPipe } from '@angular/common';
import { TimelineComponent } from '@shared/components/timeline/timeline.component';
import {
  TimelineNode,
  TimelineNodeState,
} from '@shared/components/timeline/timeline.model';
import { MenuItem } from 'primeng/api';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';

@Component({
  selector: 'app-tier',
  imports: [
    CommonModule,
    SkeletonModule,
    ProgressBarModule,
    AngularSvgIconModule,
    TimelineComponent,
    BreadcrumbComponent,
    CustomCurrencyPipe,
  ],
  templateUrl: './tier.component.html',
})
export class TierComponent implements OnInit, AfterViewInit, OnDestroy {
  rewards: Rewards | null = null;
  currency: string | undefined;
  investAmountToReachNextTier: number | null = null;
  nextTier: string | null = null;
  rewardsTimeline: TimelineNode[] = [];
  InvestementTireType = InvestementTireType;
  investmentTires: InvestementTire[] = [];
  readonly breadcrumdItems: MenuItem[] = [
    {
      label: 'Rewards',
      routerLink: '/home/rewards',
    },
    {
      label: 'Tier',
    },
  ];

  private rewardsSub = Subscription.EMPTY;

  readonly _rewardsService = inject(RewardsService);
  private readonly _ngProgressService = inject(NgProgressService);
  private readonly _profileService = inject(ProfileService);
  private readonly _decimalPipe = inject(DecimalPipe);

  ngOnInit(): void {
    this._ngProgressService.startLoader();

    this.currency = this._profileService.profile.currency;

    this.rewardsSub = this._rewardsService.rewards$.subscribe((rewards) => {
      this.rewards = rewards;
    });

    combineLatest([
      this._rewardsService.rewards$,
      this._rewardsService.investmentTires$,
    ])
      .pipe(
        filter(
          ([rewards, investmentTires]) =>
            rewards !== null &&
            rewards !== undefined &&
            investmentTires !== null &&
            investmentTires !== undefined
        )
      )
      .subscribe(([rewards, investmentTires]) => {
        this.investmentTires = investmentTires!;
        const currentTierIndex = investmentTires?.findIndex(
          (tier) => tier.type === rewards?.currentInvestementTire
        );

        const nextTier = investmentTires![currentTierIndex! + 1];
        this.nextTier = nextTier?.title ?? null;
        this.investAmountToReachNextTier = nextTier?.investToUnlock ?? null;
        this.parseRewardsTimeline();
      });

    this._rewardsService.getRewards();
    this._rewardsService.getInvestmentTires();
  }

  ngAfterViewInit(): void {
    this._ngProgressService.completeLoader();
  }

  parseRewardsTimeline(): void {
    const currentTierIndex = this.investmentTires?.findIndex(
      (tier) => tier.type === this.rewards?.currentInvestementTire
    );

    this.rewardsTimeline = [];

    this.investmentTires.forEach((tier: InvestementTire, index: number) => {
      const state =
        index < currentTierIndex
          ? TimelineNodeState.COMPLETED
          : index === currentTierIndex
          ? TimelineNodeState.ACTIVE
          : TimelineNodeState.INCOMPLETED;

      let headerExtra = '';
      let headerExtraImg = '';
      if (state === TimelineNodeState.INCOMPLETED) {
        const investToUnlock = this._decimalPipe.transform(
          tier.investToUnlock,
          '1.2-2'
        );

        headerExtra = `${this.currency} ${investToUnlock} to unlock`;
        headerExtraImg = 'assets/icons/lock.svg';
      }

      this.rewardsTimeline.push({
        header: tier.title,
        headerExtra: headerExtra,
        headerExtraImg: headerExtraImg,
        description: tier.description,
        state: state,
      });
    });
  }

  ngOnDestroy(): void {
    this.rewardsSub.unsubscribe();
  }
}
